// pages/OnboardingPage.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import confetti from 'canvas-confetti';
import 'react-phone-number-input/style.css';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import ImageUploader from '../../components/common/ImageUploader';
import {
  FiCheck,
  FiArrowRight,
  FiArrowLeft,
  FiBriefcase,
  FiPhone,
  FiShare2,
  FiImage,
  FiCheckCircle,
  FiMapPin,
  FiGlobe,
  FiMail,
  FiInstagram,
  FiFacebook,
  FiYoutube,
  FiLinkedin,
  FiAlertCircle,
  FiInfo
} from 'react-icons/fi';
import './OnboardingPage.css';

const CATEGORIES = [
  { value: 'Restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'Clothing', label: 'Clothing', icon: '👕' },
  { value: 'Jewellery', label: 'Jewellery', icon: '💎' },
  { value: 'Real Estate', label: 'Real Estate', icon: '🏠' },
  { value: 'Education', label: 'Education', icon: '📚' },
  { value: 'Salon', label: 'Salon', icon: '💇' },
  { value: 'Healthcare', label: 'Healthcare', icon: '🏥' },
  { value: 'Electronics', label: 'Electronics', icon: '📱' },
  { value: 'Furniture', label: 'Furniture', icon: '🪑' },
  { value: 'Travel', label: 'Travel', icon: '✈️' },
  { value: 'Freelancer', label: 'Freelancer', icon: '💻' },
  { value: 'Consultant', label: 'Consultant', icon: '📊' },
  { value: 'Other', label: 'Other', icon: '📌' }
];

const PALETTES = [
  { name: 'Indigo & Cyan', primary: '#4F46E5', secondary: '#06B6D4' },
  { name: 'Purple & Pink', primary: '#8B5CF6', secondary: '#EC4899' },
  { name: 'Emerald & Teal', primary: '#10B981', secondary: '#14B8A6' },
  { name: 'Sunset Orange', primary: '#EA580C', secondary: '#F59E0B' },
  { name: 'Midnight Blue', primary: '#3B82F6', secondary: '#6366F1' },
  { name: 'Rose Gold', primary: '#E11D48', secondary: '#F43F5E' }
];

const STEPS = [
  { step: 1, label: 'Basic Info', icon: FiBriefcase },
  { step: 2, label: 'Contact', icon: FiPhone },
  { step: 3, label: 'Social Media', icon: FiShare2 },
  { step: 4, label: 'Branding', icon: FiImage },
  { step: 5, label: 'Complete', icon: FiCheck }
];

// ---------- Validation schema (zod) ----------
const urlOrEmpty = z
  .string()
  .trim()
  .optional()
  .or(z.literal(''))
  .refine((val) => !val || /^https?:\/\/[^\s]+$/i.test(val), {
    message: 'Enter a valid URL starting with http:// or https://'
  });

const schema = z.object({
  businessName: z
    .string()
    .trim()
    .min(2, 'Business name must be at least 2 characters')
    .max(100, 'Business name cannot exceed 100 characters')
    .regex(/^[a-zA-Z0-9\s\-&.']+$/, 'Business name contains invalid characters'),
  category: z.string(),
  description: z.string().max(500, 'Keep the description under 500 characters').optional(),

  phone: z
    .string()
    .min(1, 'Phone number is required for contact')
    .refine((val) => isValidPhoneNumber(val || ''), 'Enter a valid phone number'),
  whatsapp: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((val) => !val || isValidPhoneNumber(val), 'Enter a valid WhatsApp number'),
  email: z.string().trim().optional().or(z.literal('')).refine(
    (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
    'Enter a valid email address'
  ),
  website: urlOrEmpty,
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),

  socialLinks: z.object({
    instagram: urlOrEmpty,
    facebook: urlOrEmpty,
    youtube: urlOrEmpty,
    linkedin: urlOrEmpty
  }),

  logo: z.string().min(1, 'Please upload a business logo'),
  coverImage: z.string().optional(),
  primaryColor: z.string(),
  secondaryColor: z.string()
});

// Fields validated per step before allowing "Next"
const STEP_FIELDS = {
  1: ['businessName', 'category', 'description'],
  2: ['phone', 'whatsapp', 'email', 'website', 'address', 'city', 'state', 'country'],
  3: ['socialLinks'],
  4: ['logo']
};

const fadeSlide = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 }
};

const shake = {
  initial: { x: 0 },
  animate: { x: [0, -8, 8, -6, 6, 0] }
};

const OnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [direction, setDirection] = useState(1);
  const { user, updateBusinessState, fetchUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const confettiFiredRef = useRef(false);

  const {
    control,
    register,
    handleSubmit,
    trigger,
    watch,
    setValue,
    formState: { errors, touchedFields }
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      businessName: '',
      category: 'Other',
      description: '',
      phone: '',
      whatsapp: '',
      email: user?.email || '',
      website: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      socialLinks: { instagram: '', facebook: '', youtube: '', linkedin: '' },
      logo: '',
      coverImage: '',
      primaryColor: '#4F46E5',
      secondaryColor: '#06B6D4'
    }
  });

  const category = watch('category');
  const primaryColor = watch('primaryColor');
  const logo = watch('logo');

  const fireConfetti = useCallback(() => {
    if (confettiFiredRef.current) return;
    confettiFiredRef.current = true;
    const duration = 1400;
    const end = Date.now() + duration;

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 65,
        origin: { x: 0, y: 0.7 },
        colors: ['#667eea', '#764ba2', '#10b981']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 65,
        origin: { x: 1, y: 0.7 },
        colors: ['#667eea', '#764ba2', '#10b981']
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  useEffect(() => {
    if (step === 5) fireConfetti();
  }, [step, fireConfetti]);

  const goNext = useCallback(async () => {
    const fieldsToValidate = STEP_FIELDS[step] || [];
    const valid = await trigger(fieldsToValidate);
    if (!valid) {
      const firstErrorKey = fieldsToValidate.find((f) => {
        if (f === 'socialLinks') return errors.socialLinks;
        return errors[f];
      });
      const message =
        firstErrorKey === 'socialLinks'
          ? 'Please check your social media links'
          : errors[firstErrorKey]?.message || 'Please fix the highlighted fields';
      showToast(message, 'error');
      return;
    }
    setDirection(1);
    setStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, trigger, errors, showToast]);

  const goBack = useCallback(() => {
    setDirection(-1);
    setStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const onSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const response = await api.post('/business', formData);
      if (response.success) {
        updateBusinessState(response.data);
        await fetchUser();
        showToast('🎉 Business setup completed successfully!', 'success');
        setDirection(1);
        setStep(5);
      }
    } catch (err) {
      showToast(err.message || 'Failed to create business profile', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinishSetup = handleSubmit(onSubmit, () => {
    showToast('Please upload a business logo before finishing', 'error');
  });

  const fieldError = (name) => touchedFields[name] && errors[name];

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        {/* Header */}
        <motion.div
          className="onboarding-header"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="onboarding-title">Setup Your Digital Business</h1>
          <p className="onboarding-subtitle">Create your professional business presence in minutes</p>
        </motion.div>

        {/* Progress */}
        <div className="onboarding-progress">
          <div className="progress-steps">
            {STEPS.map(({ step: stepNum, label, icon: Icon }) => (
              <div
                key={stepNum}
                className={`progress-step ${stepNum === step ? 'active' : ''} ${
                  stepNum < step ? 'completed' : ''
                }`}
              >
                <motion.div
                  className="step-indicator"
                  animate={{
                    scale: stepNum === step ? 1.08 : 1
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {stepNum < step ? (
                      <motion.span
                        key="check"
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                        style={{ display: 'flex' }}
                      >
                        <FiCheck />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="icon"
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        style={{ display: 'flex' }}
                      >
                        <Icon />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>
          <div className="progress-bar">
            <motion.div
              className="progress-fill"
              animate={{ width: `${((Math.min(step, 5) - 1) / 4) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="onboarding-content">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            {/* STEP 1: BASIC INFORMATION */}
            {step === 1 && (
              <motion.div
                key="step1"
                className="step-content"
                custom={direction}
                variants={fadeSlide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="step-header">
                  <h2>
                    <FiBriefcase className="step-icon" />
                    Basic Information
                  </h2>
                  <p>Tell us about your business</p>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Business Name <span className="required">*</span>
                  </label>
                  <motion.div
                    className={`input-wrapper ${fieldError('businessName') ? 'error' : ''}`}
                    animate={fieldError('businessName') ? 'animate' : 'initial'}
                    variants={shake}
                    transition={{ duration: 0.4 }}
                  >
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter your business name"
                      disabled={submitting}
                      autoFocus
                      {...register('businessName')}
                    />
                    {fieldError('businessName') && <FiAlertCircle className="input-error-icon" />}
                  </motion.div>
                  {fieldError('businessName') && (
                    <span className="error-message">{errors.businessName.message}</span>
                  )}
                  <div className="field-hint">
                    <FiInfo />
                    <span>This will be used as your business page URL</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Business Category</label>
                  <div className="category-grid">
                    {CATEGORIES.map(({ value, label, icon }) => (
                      <motion.button
                        key={value}
                        type="button"
                        className={`category-btn ${category === value ? 'active' : ''}`}
                        onClick={() => setValue('category', value, { shouldValidate: true })}
                        whileHover={{ y: -3 }}
                        whileTap={{ scale: 0.94 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                      >
                        <span className="category-icon">{icon}</span>
                        <span className="category-label">{label}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Business Description</label>
                  <textarea
                    className="form-textarea"
                    rows={4}
                    placeholder="Briefly describe what your business offers..."
                    disabled={submitting}
                    {...register('description')}
                  />
                  <div className="field-hint">
                    <FiInfo />
                    <span>Write a compelling description to attract customers</span>
                  </div>
                </div>

                <div className="step-actions single">
                  <motion.button
                    onClick={goNext}
                    className="btn-primary"
                    disabled={submitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                  >
                    Next Step <FiArrowRight />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* STEP 2: CONTACT INFORMATION */}
            {step === 2 && (
              <motion.div
                key="step2"
                className="step-content"
                custom={direction}
                variants={fadeSlide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="step-header">
                  <h2>
                    <FiPhone className="step-icon" />
                    Contact Details
                  </h2>
                  <p>Make it easy for customers to reach you</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <div className={`input-wrapper phone-input ${fieldError('phone') ? 'error' : ''}`}>
                          <PhoneInput
                            {...field}
                            international
                            defaultCountry="IN"
                            disabled={submitting}
                            placeholder="98765 43210"
                          />
                          {fieldError('phone') && <FiAlertCircle className="input-error-icon" />}
                        </div>
                      )}
                    />
                    {fieldError('phone') && (
                      <span className="error-message">{errors.phone.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">WhatsApp Number</label>
                    <Controller
                      name="whatsapp"
                      control={control}
                      render={({ field }) => (
                        <div className={`input-wrapper phone-input ${fieldError('whatsapp') ? 'error' : ''}`}>
                          <PhoneInput
                            {...field}
                            international
                            defaultCountry="IN"
                            disabled={submitting}
                            placeholder="98765 43210"
                          />
                        </div>
                      )}
                    />
                    {fieldError('whatsapp') && (
                      <span className="error-message">{errors.whatsapp.message}</span>
                    )}
                    <div className="field-hint">
                      <FiInfo />
                      <span>Optional - Use the same as phone if you use WhatsApp</span>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <div className={`input-wrapper ${fieldError('email') ? 'error' : ''}`}>
                      <FiMail className="input-icon" />
                      <input
                        type="email"
                        className="form-input"
                        placeholder="hello@example.com"
                        disabled={submitting}
                        {...register('email')}
                      />
                      {fieldError('email') && <FiAlertCircle className="input-error-icon" />}
                    </div>
                    {fieldError('email') && (
                      <span className="error-message">{errors.email.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Website</label>
                    <div className={`input-wrapper ${fieldError('website') ? 'error' : ''}`}>
                      <FiGlobe className="input-icon" />
                      <input
                        type="url"
                        className="form-input"
                        placeholder="https://example.com"
                        disabled={submitting}
                        {...register('website')}
                      />
                      {fieldError('website') && <FiAlertCircle className="input-error-icon" />}
                    </div>
                    {fieldError('website') && (
                      <span className="error-message">{errors.website.message}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Address</label>
                  <div className="input-wrapper">
                    <FiMapPin className="input-icon" />
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Street, Area, Landmark"
                      disabled={submitting}
                      {...register('address')}
                    />
                  </div>
                </div>

                <div className="form-row three">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Mumbai"
                      disabled={submitting}
                      {...register('city')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Maharashtra"
                      disabled={submitting}
                      {...register('state')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="India"
                      disabled={submitting}
                      {...register('country')}
                    />
                  </div>
                </div>

                <div className="step-actions">
                  <motion.button
                    onClick={goBack}
                    className="btn-secondary"
                    whileHover={{ x: -2 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                  >
                    <FiArrowLeft /> Back
                  </motion.button>
                  <motion.button
                    onClick={goNext}
                    className="btn-primary"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                  >
                    Next Step <FiArrowRight />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* STEP 3: SOCIAL MEDIA */}
            {step === 3 && (
              <motion.div
                key="step3"
                className="step-content"
                custom={direction}
                variants={fadeSlide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="step-header">
                  <h2>
                    <FiShare2 className="step-icon" />
                    Social Media
                  </h2>
                  <p>Connect your social profiles to build trust</p>
                </div>

                {[
                  { name: 'instagram', label: 'Instagram', Icon: FiInstagram, placeholder: 'https://instagram.com/yourbusiness' },
                  { name: 'facebook', label: 'Facebook', Icon: FiFacebook, placeholder: 'https://facebook.com/yourbusiness' },
                  { name: 'youtube', label: 'YouTube', Icon: FiYoutube, placeholder: 'https://youtube.com/@yourbusiness' },
                  { name: 'linkedin', label: 'LinkedIn', Icon: FiLinkedin, placeholder: 'https://linkedin.com/company/yourbusiness' }
                ].map(({ name, label, Icon, placeholder }) => {
                  const errMsg = errors.socialLinks?.[name]?.message;
                  return (
                    <div className="form-group" key={name}>
                      <label className="form-label">
                        <Icon className="social-icon" /> {label}
                      </label>
                      <div className={`input-wrapper ${errMsg ? 'error' : ''}`}>
                        <input
                          type="url"
                          className="form-input"
                          placeholder={placeholder}
                          disabled={submitting}
                          {...register(`socialLinks.${name}`)}
                        />
                        {errMsg && <FiAlertCircle className="input-error-icon" />}
                      </div>
                      {errMsg && <span className="error-message">{errMsg}</span>}
                    </div>
                  );
                })}

                <div className="step-actions">
                  <motion.button
                    onClick={goBack}
                    className="btn-secondary"
                    whileHover={{ x: -2 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                  >
                    <FiArrowLeft /> Back
                  </motion.button>
                  <motion.button
                    onClick={goNext}
                    className="btn-primary"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                  >
                    Next Step <FiArrowRight />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* STEP 4: BRANDING */}
            {step === 4 && (
              <motion.div
                key="step4"
                className="step-content"
                custom={direction}
                variants={fadeSlide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="step-header">
                  <h2>
                    <FiImage className="step-icon" />
                    Branding & Design
                  </h2>
                  <p>Make your business page stand out</p>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Business Logo <span className="required">*</span>
                  </label>
                  <motion.div
                    className={`image-upload-wrapper ${fieldError('logo') ? 'error' : ''}`}
                    animate={fieldError('logo') ? 'animate' : 'initial'}
                    variants={shake}
                    transition={{ duration: 0.4 }}
                  >
                    <ImageUploader
                      label="Upload Logo"
                      value={logo}
                      onChange={(url) => setValue('logo', url, { shouldValidate: true })}
                      aspectRatio="1:1"
                      maxSizeMB={2}
                      acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
                    />
                  </motion.div>
                  {fieldError('logo') && <span className="error-message">{errors.logo.message}</span>}
                  <div className="field-hint">
                    <FiInfo />
                    <span>Recommended: Square image, at least 500x500px</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Color Theme</label>
                  <div className="palette-grid">
                    {PALETTES.map((pal) => (
                      <motion.button
                        key={pal.name}
                        type="button"
                        className={`palette-btn ${primaryColor === pal.primary ? 'active' : ''}`}
                        onClick={() => {
                          setValue('primaryColor', pal.primary);
                          setValue('secondaryColor', pal.secondary);
                        }}
                        whileHover={{ y: -3 }}
                        whileTap={{ scale: 0.94 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                      >
                        <div className="palette-preview">
                          <div className="color-swatch" style={{ background: pal.primary }} />
                          <div className="color-swatch" style={{ background: pal.secondary }} />
                        </div>
                        <span className="palette-name">{pal.name}</span>
                        <AnimatePresence>
                          {primaryColor === pal.primary && (
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              exit={{ scale: 0 }}
                              className="palette-check"
                            >
                              <FiCheck />
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="step-actions">
                  <motion.button
                    onClick={goBack}
                    className="btn-secondary"
                    whileHover={{ x: -2 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                  >
                    <FiArrowLeft /> Back
                  </motion.button>
                  <motion.button
                    onClick={handleFinishSetup}
                    className="btn-primary btn-success"
                    disabled={submitting}
                    whileHover={{ scale: submitting ? 1 : 1.02 }}
                    whileTap={{ scale: submitting ? 1 : 0.98 }}
                    type="button"
                  >
                    {submitting ? (
                      <>
                        <span className="spinner"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        Complete Setup <FiCheck />
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* STEP 5: COMPLETE */}
            {step === 5 && (
              <motion.div
                key="step5"
                className="step-content step-complete"
                custom={direction}
                variants={fadeSlide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="complete-icon"
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.1 }}
                >
                  <FiCheckCircle />
                </motion.div>
                <h2>🎉 Your Business Page is Ready!</h2>
                <p>
                  Congratulations! Your digital business presence is officially live.
                  You can now manage your products, generate QR codes, and customize
                  your page from the dashboard.
                </p>

                <div className="next-steps">
                  <h3>What's Next?</h3>
                  <ul>
                    {[
                      { Icon: FiBriefcase, text: 'Add your products and services' },
                      { Icon: FiShare2, text: 'Share your business page with customers' },
                      { Icon: FiImage, text: 'Customize your page design further' }
                    ].map(({ Icon, text }, i) => (
                      <motion.li
                        key={text}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + i * 0.1 }}
                      >
                        <Icon />
                        <span>{text}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <motion.button
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary btn-lg"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Go to Dashboard <FiArrowRight />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;